import { http } from '@/http';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { i18n } from '@/main';
import {
  AuthenticationSignicat,
  AuthorizeUser,
  ChangeLang,
  UpdateUserProfile,
  UserProfile
} from '@/store/modules/account/types';
import { ApiResponse, LoggedInAndOut, PharmacyAccessesResponse } from './types';
import { EntityNames } from '@/store/modules/entityNames/types';
import {
  AlarmComment,
  AlarmsData,
  AssignDelivery,
  CapacityByPharmacyId,
  CapacityByPickupBoxId,
  CreateReservation,
  CreateReservationPharmacy,
  DeletePharmacyReservation,
  DeliveriesStatistics,
  Delivery,
  Description,
  InfoFromPickupBox,
  Pharmacy,
  PickupBox,
  PickupBoxUpdate,
  ResendSMS,
  TemperatureLog,
  TemperatureStamps,
  PickupDeletedOrRemovedDelivery,
  ToggleActive,
  ToggleLocker,
  UpdatePharmacyReservation,
  SsnDelivery,
  DestroyReservation,
  DataServiceRequest,
  VerifySSN,
  VerifyPIN,
  AlarmSensor,
  TemperatureSensorStamp,
  DataUpdateSensor,
  DataSensor,
  SendTemperatureComment,
  FeedbackTemplate
} from '@/store/modules/pharmacy/types';
import {
  LoginInfo,
  PharmacyAccessesAdd,
  UpdatePharmacyAccesses,
  UserCreate,
  UserResult,
  UserUpdate
} from '@/store/modules/users/types';
import { AppRoleResult } from '@/store/modules/roles/types';
import { createToaster } from '@meforma/vue-toaster';
import router from '@/router';
import { LocationQueryValueRaw } from 'vue-router';
import {
  DailyStatistics,
  FeedbackStatistics
} from '@/store/modules/orders/types';

const toaster = createToaster({
  type: 'error',
  duration: 3000,
  position: 'top-right'
});

export const handleAxiosError = (error: unknown): unknown => {
  const axiosError = error as AxiosError;

  const responseData = axiosError.response?.data as ApiResponse;

  for (const error of responseData.errors) {
    const errorMessage =
      axiosError.response && axiosError.response.data && responseData.message
        ? i18n.global.t(error)
        : axiosError.message;

    if (
      (!axiosError.response || axiosError.response.status !== 401) &&
      errorMessage
    ) {
      if (responseData.message === 'Validation fail') {
        toaster.show(responseData.errors[0].msg);
      } else {
        if (axiosError.response?.status !== 300) {
          toaster.show(errorMessage);
        }
      }
      if (
        axiosError.response?.status === 403 &&
        responseData.message === 'User not authorized'
      ) {
        router.push({ name: 'VerifiedUser' });
      }
      return error;
    } else {
      return Promise.reject(error);
    }
  }
};

export const handleDefaultError = (
  error: AxiosError<unknown, unknown>
): unknown => {
  const errorMessage = error.message ? error.message : 'Unknown Error';
  console.log(errorMessage);
  return Promise.reject(error);
};

// Get admin profile using firebase token
export const getProfile = async (): Promise<UserProfile | undefined> => {
  try {
    const response = await http.get('/profile');
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const writeLoggedIn = async (): Promise<LoggedInAndOut | undefined> => {
  try {
    const response = await http.post('/users/login-logs/logged-in');
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const writeLoggedOut = async (): Promise<LoggedInAndOut | undefined> => {
  try {
    const response = await http.post('/users/login-logs/logged-out');
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const getAuthenticationSignicat = async (
  businessId: string,
  deliveryId: string
): Promise<AuthenticationSignicat | undefined> => {
  try {
    const endpoint = `/strong-authentication/show-verify-link/${businessId}/${deliveryId}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
      throw new Error(error.response?.data.message);
    }
  }
};

export const authorizeUser = async (
  data: AuthorizeUser
): Promise<AuthorizeUser | undefined> => {
  try {
    const response = await http.post(
      '/strong-authentication/authenticate',
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
      throw new Error(error.response?.data.message);
    }
  }
};

export const getEntityNames = async (): Promise<EntityNames | undefined> => {
  try {
    const response = await http.get('/health/entity-names');
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const changeLang = async (
  data: ChangeLang
): Promise<ChangeLang | undefined> => {
  try {
    const response = await http.patch('/profile/change-lang', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

// Check if server is up
export const pingServer = async (): Promise<AxiosResponse | undefined> => {
  try {
    const response = await http.get('/health/ping');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

// get localization data
export const getLanguage = async (): Promise<AxiosResponse | undefined> => {
  try {
    const response = await http.get('/localizations/web');
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const setUserLang = async (lang: string): Promise<null | undefined> => {
  try {
    await http.post('/users/change-lang', { lang: lang });
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPharmacies = async (): Promise<Pharmacy[] | undefined> => {
  try {
    const endpoint = '/pharmacies/';

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPharmacyById = async (
  id: string
): Promise<Pharmacy | undefined> => {
  try {
    const endpoint = `/pharmacies/show/${id}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPharmacyServiceRequest = async (
  data: DataServiceRequest,
  pharmacyId: string
): Promise<DataServiceRequest | undefined> => {
  try {
    const response = await http.post(
      `/pharmacies/service-request/${pharmacyId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updatePharmacy = async (
  data: Pharmacy[],
  pharmacyId: string
): Promise<Pharmacy[] | undefined> => {
  try {
    const response = await http.patch(`/pharmacies/update/${pharmacyId}`, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPickupBoxByPharmacyId = async (
  id: string
): Promise<PickupBox[]> => {
  try {
    const endpoint = `/pickup-boxes/show-by-pharmacy-id/${id}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }

    return [];
  }
};

export const getPickupBoxCapacity = async (
  pharmacyId: string,
  startTime: number,
  endTime: number
): Promise<CapacityByPharmacyId | undefined> => {
  try {
    const endpoint = `/pickup-boxes/capacity-per-pharmacy/${pharmacyId}?startTime=${startTime}&endTime=${endTime}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getCapacityByPickupBoxId = async (
  boxId: string,
  startTime: number,
  endTime: number
): Promise<CapacityByPickupBoxId | undefined> => {
  try {
    const endpoint = `/pickup-boxes/capacity-per-box/${boxId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const createReservation = async (
  data: CreateReservation,
  boxId: string
): Promise<CreateReservation | undefined> => {
  try {
    const response = await http.post(`/deliveries/create/${boxId}`, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const createPharmacyReservation = async (
  data: CreateReservationPharmacy,
  pharmacyId: string
): Promise<CreateReservationPharmacy | undefined> => {
  try {
    const response = await http.post(
      `/deliveries/create-on-pharmacy/${pharmacyId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const deleteReservation = async (
  boxId: string,
  deliveryId: string
): Promise<unknown> => {
  try {
    const endpoint = `/deliveries/delete-reservation/${boxId}/${deliveryId}`;

    const response = await http.delete(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const destroyReservation = async (
  destroyReservation: DestroyReservation
): Promise<DestroyReservation | unknown> => {
  try {
    const response = await http.delete(`/deliveries/destroy`, {
      data: { ...destroyReservation }
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const deletePharmacyReservation = async (
  data: DeletePharmacyReservation,
  pharmacyId: string,
  deliveryId: string
): Promise<DeletePharmacyReservation | undefined> => {
  try {
    const response = await http.delete(
      `/deliveries/delete-reservation-on-pharmacy/${pharmacyId}/${deliveryId}`,
      { data: { reservedTime: data.reservedTime } }
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updatePharmacyReservation = async (
  data: UpdatePharmacyReservation,
  pharmacyId: string,
  deliveryId: string
): Promise<UpdatePharmacyReservation | undefined> => {
  try {
    const response = await http.patch(
      `/deliveries/update-on-pharmacy/${pharmacyId}/${deliveryId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const verifySSN = async (
  data: VerifySSN,
  deliveryId: string
): Promise<VerifySSN | undefined> => {
  try {
    const response = await http.post(
      `/deliveries/verify-ssn/${deliveryId}`,
      data
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const verifyPIN = async (
  data: VerifyPIN,
  deliveryId: string
): Promise<VerifyPIN | undefined> => {
  try {
    const response = await http.post(
      `/deliveries/verify-pin/${deliveryId}`,
      data
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const resendSMS = async (
  data: ResendSMS
): Promise<ResendSMS | undefined> => {
  try {
    const response = await http.post('/deliveries/resend-sms', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const assignDelivery = async (
  data: AssignDelivery,
  pharmacyId: string,
  deliveryId: string
): Promise<AssignDelivery | undefined> => {
  try {
    const response = await http.patch(
      `/deliveries/assign/${pharmacyId}/${deliveryId}`,
      data
    );

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getDeliveriesStatistics = async (
  pharmacyId: string,
  startTime: number,
  endTime: number
): Promise<DeliveriesStatistics[] | undefined> => {
  try {
    const endpoint = `/deliveries/statistics/per-pharmacy/${pharmacyId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=0`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPickupBoxById = async (
  boxId: string
): Promise<InfoFromPickupBox | undefined> => {
  try {
    const response = await http.get(`/pickup-boxes/show/${boxId}`);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getTemperaturesById = async (
  boxId: string
): Promise<TemperatureStamps | undefined> => {
  try {
    const response = await http.get(
      `/temperatures/temperatures-stamps/${boxId}`
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const writeTemperaturesById = async (
  boxId: string
): Promise<LoggedInAndOut | undefined> => {
  try {
    const response = await http.post(
      `/temperatures/temperatures-stamps/${boxId}`
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const updatePickupBox = async (
  data: PickupBoxUpdate,
  boxId: string
): Promise<PickupBoxUpdate | undefined> => {
  try {
    const response = await http.patch(`/pickup-boxes/update/${boxId}`, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateProfile = async (
  data: UpdateUserProfile
): Promise<UpdateUserProfile | undefined> => {
  try {
    const response = await http.patch('/profile/update', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getSensor = async (
  pharmacyId: string,
  sensorId: string,
  startTime: number,
  endTime: number
): Promise<DataSensor | undefined> => {
  try {
    const response = await http.get(
      `/temperatures/sensors/read/${pharmacyId}/${sensorId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}`
    );

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateSensor = async (
  data: DataUpdateSensor,
  pharmacyId: string,
  sensorId: string
): Promise<DataUpdateSensor | undefined> => {
  try {
    const response = await http.patch(
      `/temperatures/sensors/update/${pharmacyId}/${sensorId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getTemperatureSensorStamp = async (
  boxId: string,
  last: boolean
): Promise<TemperatureSensorStamp | undefined> => {
  try {
    const response = await http.get(
      `/temperatures/temperatures-stamps/${boxId}?last=${last}`
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const sendTemperatureSensorsStamp = async (
  boxId: string
): Promise<unknown> => {
  try {
    const response = await http.post(
      `/temperatures/temperatures-stamps/${boxId}`
    );

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getAlarmsSensor = async (
  sensorSn: string,
  startTime: string,
  endTime: string
): Promise<AlarmSensor[] | undefined> => {
  try {
    const response = await http.get(
      `/alarms/show-by-sensor-sn/${sensorSn}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}`
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const sendCommentTemperatures = async (
  data: SendTemperatureComment,
  pharmacyId: string,
  sensorId: string
): Promise<SendTemperatureComment | undefined> => {
  try {
    const response = await http.post(
      `/temperatures/sensors/add-comment/${pharmacyId}/${sensorId}`,
      data
    );

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

// Users

export const getUsers = async (
  lastKey: string | null,
  pageSize: number,
  pharmacyId: string
): Promise<UserResult[] | undefined> => {
  try {
    let endpoint = '/users/';

    if (!lastKey) {
      endpoint += `?pageSize=${pageSize}&pharmacyId=${pharmacyId}`;
    } else {
      endpoint += `?pageSize=${pageSize}&lastKey=${lastKey}`;
    }

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getUserById = async (
  id: string
): Promise<UserResult | undefined> => {
  try {
    const endpoint = `/users/show/${id}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const createUser = async (
  data: UserCreate
): Promise<UserCreate | undefined> => {
  try {
    const response = await http.post('/users/create', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const toggleActivePickupBox = async (
  data: ToggleActive,
  boxId: string
): Promise<ToggleActive | undefined> => {
  try {
    const response = await http.patch(
      `/pickup-boxes/toggle-active/${boxId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const toggleLockerPickupBox = async (
  data: ToggleLocker,
  boxId: string
): Promise<ToggleLocker | undefined> => {
  try {
    const response = await http.patch(
      `/pickup-boxes/toggle-locker/${boxId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateUser = async (
  data: UserUpdate,
  userId: string
): Promise<UserUpdate | undefined> => {
  try {
    const response = await http.patch(`/users/update/${userId}`, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getPharmacyAccesses =
  async (): Promise<PharmacyAccessesResponse> => {
    try {
      const response = await http.get('/users/pharmacy-accesses');
      return {
        status: response.status,
        data: response.data.result
      };
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as ApiResponse;
      const resultData: LocationQueryValueRaw = responseData.result;

      return {
        status: axiosError.response?.status,
        data: resultData
      };
    }
  };

export const updatePharmacyAccess = async (
  data: UpdatePharmacyAccesses
): Promise<UpdatePharmacyAccesses | undefined> => {
  try {
    const response = await http.patch('/users/pharmacy-accesses', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      await handleAxiosError(error);
    }
  }
};

export const addPharmacyAccesses = async (
  data: PharmacyAccessesAdd
): Promise<PharmacyAccessesAdd | undefined> => {
  try {
    const response = await http.patch(`/users/add-pharmacy-access`, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const removePharmacyAccess = async (
  userId: string
): Promise<unknown> => {
  try {
    const response = await http.patch(
      `/users/remove-pharmacy-access/${userId}`
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getLoginInfo = async (
  pharmacyId: string,
  limit: number,
  lastTimestamp: string
): Promise<LoginInfo[] | undefined> => {
  try {
    let endpoint = `/users/login-logs/${pharmacyId}`;

    if (lastTimestamp) {
      endpoint += `?limit=${limit}&lastTimestamp=${lastTimestamp}`;
    }

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

// Roles
export const getAppRoles = async (): Promise<AppRoleResult[] | undefined> => {
  try {
    const endpoint = '/roles/app-roles';
    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getDeliveryHistory = async (
  boxId_or_pharmacyId: string,
  startTime: string,
  endTime: string
): Promise<Delivery[] | undefined> => {
  try {
    const endpoint = `/deliveries/show/${boxId_or_pharmacyId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getDeliveryFindBySSN = async (
  ssn: string,
  unassigned: boolean
): Promise<Delivery[] | undefined> => {
  try {
    const endpoint = `/deliveries/find-by-ssn?ssn=${ssn}&unassigned=${unassigned}`;
    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getDeliveryShowDecodedSsn = async (
  deliveryId: string,
  reservedTime: string,
  pickupboxId?: string,
  docId?: string,
  pharmacyId?: string
): Promise<SsnDelivery | undefined> => {
  try {
    let endpoint = `/deliveries/show-decoded-ssn/${deliveryId}?reservedTime=${reservedTime}`;
    if (pickupboxId) {
      endpoint += `&pickupboxId=${pickupboxId}`;
    } else if (pharmacyId) {
      endpoint += `&pharmacyId=${pharmacyId}`;
    } else {
      endpoint += `&docId=${docId}`;
    }

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const deliveryDeletedOrRemovedPickup = async (
  data: PickupDeletedOrRemovedDelivery
): Promise<Delivery[] | undefined> => {
  try {
    const endpoint = '/deliveries/pickup-deleted-or-removed-delivery';

    const response = await http.patch(endpoint, data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateDescription = async (
  data: Description,
  deliveryId: string
): Promise<Description | undefined> => {
  try {
    const response = await http.patch(
      `/deliveries/update-description/${deliveryId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getAlarms = async (
  boxId: string,
  startTime: string,
  endTime: string,
  alarmType: string
): Promise<AlarmsData[] | undefined> => {
  try {
    let endpoint = `/alarms/show-by-box-id/${boxId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=0`;

    if (alarmType) {
      endpoint += `&alarmType=${alarmType}`;
    }

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getTemperatureLog = async (
  boxId: string,
  startTime: string,
  endTime: string
): Promise<TemperatureLog | undefined> => {
  try {
    const endpoint = `/temperatures/logs-per-box/${boxId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateAlarmComment = async (
  data: AlarmComment
): Promise<AlarmComment | undefined> => {
  try {
    const response = await http.post('/alarms/comment', data);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getFeedbackStatistics = async (
  pharmacyId: string,
  startTime: string,
  endTime: string,
  minutesInterval: number,
  chartFields: string
): Promise<FeedbackStatistics | undefined> => {
  try {
    const endpoint = `/feedback/statistics/${pharmacyId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}&minutesInterval=${minutesInterval}&chartFields=${chartFields}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getFeedbackStatisticsWeek = async (
  pharmacyId: string,
  startTime: string,
  endTime: string,
  minutesInterval: number,
  chartFields: string
): Promise<FeedbackStatistics | undefined> => {
  try {
    const endpoint = `/feedback/statistics/${pharmacyId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}&minutesInterval=${minutesInterval}&chartFields=${chartFields}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const updateFeedbackTemplate = async (
  data: FeedbackTemplate,
  pharmacyId: string
): Promise<FeedbackTemplate | undefined> => {
  try {
    const response = await http.patch(
      `/pharmacies/update-feedback-template/${pharmacyId}`,
      data
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const getCustomersSatisfaction = async (
  pharmacyId: string,
  startTime: string,
  endTime: string,
  otherPeriod: string
): Promise<DailyStatistics | undefined> => {
  try {
    const endpoint = `/feedback/daily-statistics/${pharmacyId}?startTime=${startTime}&endTime=${endTime}&timezoneOffset=${new Date().getTimezoneOffset()}&otherPeriod=${otherPeriod}`;

    const response = await http.get(endpoint);
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error);
    }
  }
};

export const setHeaderToken = (token: string): void => {
  http.defaults.headers.common['AuthToken'] = token;
};

export const deleteHeaderToken = (): void => {
  delete http.defaults.headers.common['AuthToken'];
};
